import {useCallback, useState, useMemo} from 'react';
import {Outlet, useLocation} from 'react-router';
import {
  Grid,
  GridCol,
  Button,
  Select,
  SelectOption,
  NavHorizontal,
  PageContainer,
  NavItem,
  Checkbox,
  Divider,
  spaceHelpers
} from '@flixbus/honeycomb-react';
import {
  Icon,
  IconDelete,
  IconDeleteSolid,
  IconOfferSolid
} from '@flixbus/honeycomb-icons-react';
import ContentLoader from '../../Common/ContentLoader';
import {
  addCountryCostEntryYear,
  deleteCountryCostEntryYear
} from '../store/thunks';
import {
  resetCountrySpecificData,
  resetCountryYearSpecificData
} from '../store/slice';
import CountryPanel from '../../Countries/components/CountryPanel/CountryPanel';
import {NavLink} from 'react-router';
import navHorizontalOptions from '../constants/navHorizontalOptions';
import {RegionalCostsPath} from '../constants/routePaths';
import {confirm} from '../../Common/Confirm';
import {useToast} from '../../Common/toasts-container';
import {getCountryName} from '../../Home/utils';
import {useUpdateCountryQualityCheck} from '../hooks/useUpdateCountryQualityCheck';
import PowerBiReportLink from '@/components/PowerBiReportLink/PowerBiReportLink';
import {POWER_BI_REPORT_REGIONAL_YOY_URL} from '@/constants/PowerBiReportUrls';
import {initCountryObject} from '@/Pages/Countries/store/data';
import {useCountries} from '@/Pages/Countries/hooks/useCountries';
import {useCountryCostsQueryParams} from '@/Pages/RegionalCosts/hooks/useCountryCostsQueryParams';
import {useCountriesCostYears} from '@/Pages/RegionalCosts/hooks/useCountriesCostYears';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {CountryPanelFormData} from '@/Pages/Countries/components/CountryPanel/CountryPanel.types';
import {Country} from '@/Pages/Countries/store/types';

const RegionalCosts: React.FC = () => {
  const qualityChecked = useAppSelector(
    ({regionalCosts}) => regionalCosts.qualityChecked
  );
  const dispatch = useAppDispatch();

  const {countries, refetchCountries} = useCountries();
  const {countriesCostYears, refetchCountriesCostYears} =
    useCountriesCostYears();
  const {countryId, handleChangeCountryId, year, handleChangeYear} =
    useCountryCostsQueryParams();

  const {1: space1, 4: space4} = spaceHelpers();
  const {addToast} = useToast();
  const location = useLocation();

  const [newCountry, setNewCountry] = useState<CountryPanelFormData | null>(
    null
  );
  const onClosePanel = useCallback(
    async (country?: Country) => {
      // reset panel
      setNewCountry(null);

      // refetch countries if a country was successfully created or updated
      if (country) {
        await Promise.all([refetchCountries(), refetchCountriesCostYears()]);
        handleChangeCountryId(country.id);
      }
    },
    [handleChangeCountryId, refetchCountries, refetchCountriesCostYears]
  );

  const ignoreQualityCheckValidationConfirmation = useCallback(
    () =>
      confirm({
        title: 'Some mandatory fields are empty',
        message: 'Please go back and specify all the values',
        acceptBtnText: 'Proceed anyway',
        cancelBtnText: 'Go back'
      }),
    []
  );
  const {updateQualityCheck} = useUpdateCountryQualityCheck(
    ignoreQualityCheckValidationConfirmation
  );

  const countriesOptions = useMemo(
    () =>
      (countries ?? []).map(country => (
        <SelectOption key={country.id} value={country.id}>
          {country.name}
        </SelectOption>
      )),
    [countries]
  );

  const yearsOptions = useMemo(() => {
    const years =
      countriesCostYears?.find(
        countryYears => countryYears.countryId === countryId
      )?.years ?? [];
    return years.map(year => (
      <SelectOption key={year} value={year}>
        {year}
      </SelectOption>
    ));
  }, [countriesCostYears, countryId]);

  const handleAddNewCountryYear = useCallback(
    (countryId: string) => {
      dispatch(addCountryCostEntryYear(countryId, handleChangeYear));
    },
    [dispatch, handleChangeYear]
  );

  if (!countries) return <ContentLoader />;

  const onDeleteCountryCostEntryYear = async () => {
    const countryName = getCountryName(countries, countryId);
    if (
      await confirm({
        PopupIcon: IconDelete,
        title: `You are about to delete all cost data for country ${countryName} and year ${year}`,
        message: `Do you wish to proceed ?`
      })
    ) {
      dispatch(deleteCountryCostEntryYear(countryId, year, addToast));
    }
  };

  const yearSelectionIsAvailable = [
    RegionalCostsPath.vehicleCosts,
    RegionalCostsPath.driverCosts
  ].includes(location.pathname.slice(1)); // remove forward slash

  return (
    <PageContainer extraClasses="max-width-1500">
      <div className="header-section">
        <Grid>
          <GridCol size={3}>
            <div className="regional-costs-countries">
              <div>
                <Select
                  id="regional-costs-select-country"
                  label="Country"
                  value={countryId ?? ''}
                  placeholder="Select a Country"
                  onChange={e => {
                    dispatch(resetCountrySpecificData());
                    handleChangeCountryId(e.target.value);
                  }}
                >
                  {countriesOptions}
                </Select>
              </div>
              <div>
                <Button
                  display="square"
                  aria-label="New country"
                  onClick={() => setNewCountry(initCountryObject)}
                >
                  <Icon InlineIcon={IconOfferSolid} aria-hidden="true" />
                </Button>
              </div>
            </div>
          </GridCol>
          {yearSelectionIsAvailable ? (
            <GridCol size={3}>
              <div className="regional-costs-years">
                <div>
                  <Select
                    disabled={!countryId}
                    id="regional-costs-years"
                    label="Calendar year"
                    value={year ?? ''}
                    placeholder="Select a year"
                    onChange={e => {
                      dispatch(resetCountryYearSpecificData());
                      handleChangeYear(Number(e.target.value));
                    }}
                  >
                    {yearsOptions}
                  </Select>
                </div>
                <div>
                  <Button
                    display="square"
                    aria-label="New year"
                    disabled={!countryId}
                    onClick={() =>
                      countryId && handleAddNewCountryYear(countryId)
                    }
                  >
                    <Icon InlineIcon={IconOfferSolid} aria-hidden="true" />
                  </Button>
                  <Button
                    display="square"
                    aria-label="Delete year"
                    disabled={!countryId || !year}
                    onClick={onDeleteCountryCostEntryYear}
                  >
                    <Icon InlineIcon={IconDeleteSolid} aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </GridCol>
          ) : null}
        </Grid>

        <div className={space4.top}>
          <Grid
            align="center"
            extraClasses={yearSelectionIsAvailable && year ? '' : 'hidden'}
          >
            <GridCol key="grid-nested-checkbox" size={8}>
              <Checkbox
                id="regional-costs-year-quality-check"
                checked={qualityChecked}
                value={qualityChecked.toString()}
                label={`${year} regional costs database completed`}
                aria-label="regional-costs-year-quality-check"
                onChange={event => updateQualityCheck(event.target.checked)}
              />
            </GridCol>
            <GridCol key="grid-nested-link" size={4} extraClasses="justify-end">
              <PowerBiReportLink
                href={POWER_BI_REPORT_REGIONAL_YOY_URL}
                page="Regional Costs"
                label="Analyse YoY development"
              />
            </GridCol>
          </Grid>
        </div>

        <Grid>
          <GridCol size={12}>
            <div className={`${space4.top} nav-horizontal-container`}>
              <NavHorizontal
                extraClasses={space1.bottom}
                aria-label="Regional Costs Navigation"
              >
                {navHorizontalOptions.map(item => (
                  <NavItem
                    key={item.path}
                    to={{pathname: `/${item.path}`, search: location.search}}
                    RouterLink={NavLink}
                    end
                  >
                    {item.name}
                  </NavItem>
                ))}
              </NavHorizontal>
            </div>
          </GridCol>
        </Grid>
      </div>

      <Divider extraClasses="full-width-divider" />

      <div className={space4.top}>
        <Outlet />
      </div>

      <CountryPanel initialCountry={newCountry} onClose={onClosePanel} />
    </PageContainer>
  );
};

export default RegionalCosts;
